import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { __MANAGEMENT_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-old-http-fetch';
import { generatePath, useNavigate } from 'react-router-dom';
import { HttpResponseList } from 'shell/models/http-response';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { initialValuesService } from '..';
import { useFormik } from 'formik';
import { FiltersFormService, ServiceClient } from './types';
import { transformDataset } from './utils';
import { AlertModal } from 'system/components/shared/delete-alert/types';
import DeleteAlertModal from 'system/components/shared/delete-alert';
import DeleteSuccessModal from 'system/components/shared/delete-success';
import {
  FiltersService,
  // ServiceMenuItems,
  // ServiceOperatorModal,
} from 'system/components/services/list';
import { Service } from 'shell/models/service';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { CommonServiceCrumbs } from '../constants';
import { useActionAuthorization } from 'shell/hooks/use-action-authorization';
import { Operator } from 'shell/models/operator';
// import { ItemVM } from 'shell/view-models/item';
// import { SelectChangeEvent } from '@mui/material';
import ListHeader from 'shell/components/shared/list-header';
import { authsObj, headCells, validationSchemaServicesFilter } from './constants';
import Table from 'shell/components/shared/table';
import { Typography } from '@mui/material';

interface OperatorModal<T> extends AlertModal<T> {
  choosenElement?: string;
}

const ServicesPage = (): React.ReactElement => {
  const [deleteAlertModal, setDeleteAlertModal] = useState<OperatorModal<ServiceClient>>({
    element: null,
    open: false,
    choosenElement: '',
  });

  // const [operatorModal, setOperatorModal] = useState<OperatorModal<ServiceClient>>({
  //   element: null,
  //   open: false,
  //   choosenElement: '',
  // });

  const [deleteSuccessModal, setDeleteSuccessModal] = useState<AlertModal<ServiceClient>>({
    element: null,
    open: false,
  });
  const { delete: deleteService } = useHttpFetch(`${__MANAGEMENT_API_URL__}/servizi`);

  const { get: getOperatorsByService } = useHttpFetch<HttpResponseList<Operator>>(
    `${__MANAGEMENT_API_URL__}/operatori/servizi`,
    {
      data: {},
    }
  );

  const { userProfile } = useUserProfile();

  const filterTable = useFilterTable<FiltersFormService>();

  const {
    data,
    get: getServices,
    error,
  } = useHttpFetch<HttpResponseList<Service>>(`${__MANAGEMENT_API_URL__}/servizi`, {
    data: {
      payload: {
        list: [],
        totalRows: 0,
      },
    },
  });

  const services: Array<ServiceClient> = useMemo(() => {
    if (!!error || !data?.payload) {
      return [];
    }
    return data.payload.list.map((service) => ({
      code: service.key.codiceServizio,
      description: service.descrizione,
    }));
  }, [data, error]);

  const onSubmit = useCallback(
    (formValues: FiltersFormService) => {
      const queryString = transformDataset({ ...formValues, page: 0 });
      filterServiceForm.setFieldValue('page', 0);
      getServices(queryString);
    },
    [getServices]
  );
  const filterServiceForm = useFormik({
    initialValues: initialValuesService,
    validationSchema: validationSchemaServicesFilter,
    onSubmit,
  });

  useEffect(() => {
    filterServiceForm.setValues(filterTable.values || initialValuesService);
    const queryString = transformDataset(filterTable.values || initialValuesService);
    getServices(queryString);
  }, []);

  useEffect(() => {
    return () => filterTable.setValues(filterServiceForm.values);
  }, [filterServiceForm.values]);

  const count: number = useMemo(() => {
    if (!!error || !data?.payload || !data?.payload.pageInfo) {
      return 0;
    }
    return data.payload.list.length;
  }, [error, data?.payload]);

  const navigate = useNavigate();

  const auths = useActionAuthorization(authsObj);

  const body = useMemo(() => {
    if (!services) {
      return [];
    }
    return services.map((service) =>
      service.code === userProfile.datiLogin?.profile?.servizio?.key?.codiceServizio
        ? {
            ...service,
            hidden: {
              delete: true,
            },
          }
        : service
    );
  }, [services]);

  const onVisibilityPress = useCallback(
    (cell): Promise<ServiceClient> => {
      return new Promise((resolve) => {
        navigate(generatePath('/sistema/servizi/dettaglio/:id', { id: cell.code }));
        resolve(cell);
      });
    },
    [navigate]
  );

  const onEditPress = useCallback(
    (cell): Promise<ServiceClient> => {
      return new Promise((resolve) => {
        navigate(generatePath('/sistema/servizi/modifica/:id', { id: cell.code }));
        resolve(cell);
      });
    },
    [navigate]
  );

  const onTrashPress = useCallback(
    (cell): Promise<ServiceClient> => {
      return new Promise((resolve) => {
        setDeleteAlertModal({ open: true, element: cell, choosenElement: '' });
        resolve(cell);
      }) as Promise<ServiceClient>;
    },
    [getOperatorsByService, setDeleteAlertModal]
  );

  const handleChangePage = useCallback(
    (newPage) => {
      filterServiceForm.setFieldValue('page', newPage);
    },
    [filterServiceForm.setFieldValue]
  );

  const handleChangeRowsPerPage = useCallback(
    (rowsPerPage) => {
      const newDataset = { ...filterServiceForm.values, rowsPerPage: rowsPerPage || 0, page: 0 };
      filterServiceForm.setValues(newDataset);
    },
    [filterServiceForm.setValues, filterServiceForm.values]
  );

  const onSort = useCallback(
    (orders) => {
      const newDataset = { ...filterServiceForm.values, orders };
      const queryString = transformDataset(newDataset);
      return getServices(queryString).then(() => {
        filterServiceForm.setFieldValue('orders', orders);
      });
    },
    [filterServiceForm.setFieldValue, getServices, filterServiceForm.values]
  );

  return (
    <Fragment>
      <Breadcrumbs items={CommonServiceCrumbs} />
      <ListHeader
        addBtnAriaLabel="Aggiungi nuovo servizio"
        addBtnLabel="Aggiungi nuovo servizio"
        canAdd={auths.create}
        onAddBtnClick={() => {
          navigate('/sistema/servizi/nuovo');
        }}
        title="Gestione Servizi"
      />
      <div className="pmi-my-3">
        <FiltersService filterServiceForm={filterServiceForm} />
      </div>
      {data?.payload ? (
        body?.length > 0 ? (
          <Table
            onSort={onSort}
            head={headCells}
            orders={filterServiceForm.values.orders}
            activeActions={auths}
            page={filterServiceForm.values.page}
            rowsPerPage={filterServiceForm.values.rowsPerPage}
            body={body}
            onVisibilityPress={onVisibilityPress}
            onEditPress={onEditPress}
            onTrashPress={onTrashPress}
            count={count || 0}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ariaLabel="Lista Servizi"
          />
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
      <DeleteAlertModal
        title="Cancellazione Servizio"
        message={`Confermare la cancellazione del servizio ${
          deleteAlertModal.element?.description || ''
        }?`}
        open={deleteAlertModal.open}
        onClose={() => setDeleteAlertModal({ open: false, element: null })}
        onYes={() => {
          const newCodiceServizio = !deleteAlertModal.choosenElement
            ? ''
            : `?newCodiceServizio=${deleteAlertModal.choosenElement}`;
          deleteService(`/${deleteAlertModal.element?.code}${newCodiceServizio}`).then(() => {
            setDeleteAlertModal({ open: false, element: null });
            setDeleteSuccessModal({ open: true, element: deleteAlertModal.element });
          });
        }}
        onNo={() => setDeleteAlertModal({ open: false, element: null })}
      />
      {/* <ServiceOperatorModal
        open={operatorModal.open}
        onClose={() => setOperatorModal({ open: false, element: null })}
        onYes={() => {
          setDeleteAlertModal({
            open: true,
            element: operatorModal.element,
            choosenElement: operatorModal.choosenElement,
          });
          setOperatorModal({ open: false, element: null, choosenElement: '' });
        }}
        onNo={() => setOperatorModal({ open: false, element: null })}
        disabled={!operatorModal.choosenElement}
      >
        <ServiceMenuItems
          items={services
            .filter((service) => service.code !== operatorModal.element?.code)
            .map(
              (service) =>
                ({
                  id: service.code,
                  value: service.description,
                } as ItemVM)
            )}
          value={operatorModal.choosenElement || ''}
          onChange={(event: SelectChangeEvent<string>) => {
            const newValue = event.target.value as string;
            setOperatorModal((oldState) => ({ ...oldState, choosenElement: newValue }));
          }}
        />
      </ServiceOperatorModal> */}
      <DeleteSuccessModal
        open={deleteSuccessModal.open}
        title="Cancellazione Servizio"
        message={`La cancellazione del servizio ${deleteSuccessModal.element?.description} è avvenuta con successo.`}
        onClose={() => {
          const queryString = transformDataset({ ...filterServiceForm.values, page: 0 });
          getServices(queryString).then(() =>
            setDeleteSuccessModal({ open: false, element: null })
          );
        }}
        onOk={() => {
          const queryString = transformDataset({ ...filterServiceForm.values, page: 0 });
          getServices(queryString).then(() =>
            setDeleteSuccessModal({ open: false, element: null })
          );
        }}
      />
    </Fragment>
  );
};

ServicesPage.displayName = 'Services page component';

export default ServicesPage;
